import React, { useEffect } from 'react';
import './App.css';
import VConsole from './components/VConsole';
import LoadingSpinner from './components/LoadingSpinner';
// import TestApiClient from './components/TestApiClient';
import TermsAgreement from './components/TermsAgreement';
import CampaignNotStarted from './components/CampaignNotStarted';
import CampaignEnded from './components/CampaignEnded';
import InvalidPromoLink from './components/InvalidPromoLink';
import DeepLinkCodeBadge, { resolveDeepLinkDisplayCode } from './components/DeepLinkCodeBadge';
import RewardCongratulations from './components/RewardCongratulations';
// import TermsAgreementTest from './components/TermsAgreementTest';
import { API_CONFIG } from './config/api';
import {
  setUser,
  setTelegramInitData,
  setIsTelegramAvailable,
  setMessage,
  initTelegram,
  setTelegramStatus,
  setStartParam,
} from './stores';
import {
  useUserStore,
  useActivationStore,
  useTelegramStore,
  useTermsStore,
  useSessionStore,
  useCampaignStore,
} from './hooks/useStores';
import { CancelResponse, ContactResponse } from './types';
import { TELEGRAM_CONFIG } from './config/telegramConfig';
import { isInvalidPromoLinkMessage } from './utils/promoMessages';

export interface TgButton 
{
  text: string;
  color: string;
  textColor: string;
  isVisible: boolean;
  isActive: boolean;
  isProgressVisible: boolean;
  setText: (text: string) => void;
  onClick: (callback: () => void) => void;
  show: () => void;
  hide: () => void;
  enable: () => void;
  disable: () => void;
  showProgress: (leaveActive?: boolean) => void;
  hideProgress: () => void;
};
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        lockOrientation: () => void;
        openTelegramLink: (url: string) => void;
        showAlert: (message: string, callback?: () => void) => void;
        showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void;
        MainButton: TgButton;
        SecondaryButton?: TgButton;
        showScanQrPopup: (params?: {text: string}, callback?: (result: string) => void) =>  boolean | void;
        closeScanQrPopup: () => void;
        requestContact: (callback: (success: boolean, info: ContactResponse | CancelResponse) => void) => void;
        initData: string;
        initDataUnsafe: {
          start_param?: string;
          chat_instance?: string;
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
          };
        };
      };
    };
  }
}

function App() {
  // Получаем состояние из stores
  const user = useUserStore();
  const activation = useActivationStore();
  const telegram = useTelegramStore();
  const terms = useTermsStore();
  
  // Логирование для отладки annotation
  console.log('Current activation.annotation:', activation.annotation);
  const session = useSessionStore();
  const campaign = useCampaignStore();
  
  // Отладочная информация (только в debug режиме)
  if (API_CONFIG.debug) {
    console.log('Current telegram state in component:', telegram);
    console.log('Current campaign state in component:', campaign);
  }
  
  const promoImageUrl = campaign.settings?.mainImageUrl || campaign.settings?.welcomeImageUrl;
  const needsPhoneVerification =
    !!campaign.settings &&
    !campaign.settings.phoneExists &&
    !user.userHasPhone;
  const deepLinkDisplayCode = resolveDeepLinkDisplayCode(
    telegram.startParam,
    campaign.settings?.deepLinkCode
  );
  const showDeepLinkCode =
    !!campaign.settings?.skipQrScan &&
    !needsPhoneVerification &&
    !!deepLinkDisplayCode;

  // Инициализация при загрузке компонента
  useEffect(() => {
    const initializeApp = () => {
      // Проверяем доступность Telegram Web App API
      if (window.Telegram?.WebApp?.initData) {
        setIsTelegramAvailable(true);
        
        // Сохраняем initData
        const initData = window.Telegram.WebApp.initData;
        const initDataUnsafe = window.Telegram.WebApp.initDataUnsafe;
        setTelegramInitData(initData);
        
        // Извлекаем start_param из initDataUnsafe
        const startParam = initDataUnsafe?.start_param;
        if (startParam) {
          setStartParam(startParam);
          if (API_CONFIG.debug) {
            console.log('Start param loaded:', startParam);
          }
        }
        
        // Получаем данные пользователя
        const userData = initDataUnsafe?.user;
        if (!user.user && userData) {
          setUser(userData);
          if (API_CONFIG.debug) {
            console.log('User data loaded:', userData);
          }
        } else {
          if (API_CONFIG.debug) {
            console.log('No user data in initDataUnsafe');
          }
        }
        
        // Инициализируем Telegram Web App только если еще не инициализирован
        if (!telegram.isInitialized) {
          if (API_CONFIG.debug) {
            console.log('Starting Telegram Web App initialization...');
          }
          initTelegram();
        } else {
          if (API_CONFIG.debug) {
            console.log('Telegram Web App already initialized, skipping...');
          }
        }
        
        if (telegram.isInitialized && !telegram.startParam) {
          window.Telegram?.WebApp?.showAlert(
            'Не указан параметр запуска. Откройте mini app по ссылке из акции или SMS.'
          );
        }
               
      } else {
        setTelegramStatus('Telegram Web App недоступен');
        if (API_CONFIG.debug) {
          console.log('Telegram Web App not available');
        }
      }
    };
    initializeApp();
  }, [user.user, terms.isVisible, telegram.isInitialized, telegram.startParam, campaign.settings]);

  const outsideTelegram = !window.Telegram?.WebApp?.initData;

  // Обработка истекшего сеанса (централизованная)
  useEffect(() => {
    console.log('Session expired useEffect triggered, session.isSessionExpired:', session.isSessionExpired);
    if (session.isSessionExpired) {
      console.log('Session expired detected, calling handleSessionExpired');
      handleSessionExpired();
    }
  }, [session.isSessionExpired]);

  // Применяем цвета кампании
  useEffect(() => {
    if (campaign.settings?.buttonColor) {
      document.documentElement.style.setProperty('--campaign-button-color', campaign.settings.buttonColor);
      // Создаем более темный оттенок для hover эффекта
      const hoverColor = adjustColorBrightness(campaign.settings.buttonColor, -20);
      document.documentElement.style.setProperty('--campaign-button-hover-color', hoverColor);
    }
  }, [campaign.settings?.buttonColor]);

  
  // Обрабатываем termsConfirm из настроек кампании
  useEffect(() => {
    if (campaign.settings?.termsConfirm === true) {
      // Если пользователь уже принял условия по этой акции, скрываем TermsAgreement
      if (API_CONFIG.debug) {
        console.log('User already agreed to terms for this campaign');
      }
      // Здесь можно добавить логику для автоматического скрытия TermsAgreement
      // или установки соответствующего состояния
    }
  }, [campaign.settings?.termsConfirm]);

  // Функция для изменения яркости цвета
  const adjustColorBrightness = (color: string, amount: number): string => {
    // Удаляем # если есть
    const hex = color.replace('#', '');
    
    // Конвертируем в RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Изменяем яркость
    const newR = Math.max(0, Math.min(255, r + amount));
    const newG = Math.max(0, Math.min(255, g + amount));
    const newB = Math.max(0, Math.min(255, b + amount));
    
    // Конвертируем обратно в hex
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Обработчик истекшего сеанса
  const handleSessionExpired = () => {
    console.log('handleSessionExpired called');
    const telegramWebApp = window.Telegram?.WebApp;
    console.log('Telegram WebApp available:', !!telegramWebApp);
    console.log('showAlert available:', !!telegramWebApp?.showAlert);
    console.log('close available:', !!telegramWebApp?.close);
    
    if (telegramWebApp?.showAlert && telegramWebApp?.close) {
      console.log('Using Telegram WebApp alert and close');
      // Показываем уведомление пользователю
      telegramWebApp.showAlert(
        'Сеанс истек. Приложение будет закрыто.',
        () => {
          console.log('Alert callback executed, closing app');
          // Закрываем бота после показа уведомления
          telegramWebApp.close();
        }
      );
    } else {
      console.log('Using fallback alert');
      // Если Telegram Web App недоступен, просто показываем сообщение
      setMessage('Сеанс истек. Приложение будет закрыто.');
      setTimeout(() => {
        // В браузере просто показываем сообщение
        alert('Сеанс истек. Обновите страницу.');
      }, 1000);
    }
  };

  

  return (
    <div className="App" style={{ background: TELEGRAM_CONFIG.backgroundColor }}>
        {/* Глобальный индикатор загрузки */}
        <LoadingSpinner />

        {!campaign.settings && !campaign.error && telegram.startParam && (
          <div className="app-status-message">Загружаем акцию...</div>
        )}

        {outsideTelegram && (
          <div className="app-status-message">
            Откройте ссылку в приложении Telegram на телефоне.
          </div>
        )}

        {campaign.error && !campaign.settings && isInvalidPromoLinkMessage(campaign.error) && (
          <InvalidPromoLink />
        )}

        {campaign.error && !campaign.settings && !isInvalidPromoLinkMessage(campaign.error) && (
          <div className="app-status-message app-status-error">{campaign.error}</div>
        )}
        
        {/* Проверяем статус кампании */}
        {campaign.settings && campaign.settings.isActive === false && (
          <CampaignEnded />
        )}
        
        {campaign.settings && campaign.settings.isActive === null && (
          <CampaignNotStarted />
        )}
        
        {/* Показываем страницу поздравления с наградой */}
        {activation.showRewardCongratulations && (
          <RewardCongratulations 
            message={activation.message || 'Поздравляем! Вы получили награду!'}   
            annotation={activation.annotation}
          />
        )}
        
        {/* Показываем TermsAgreement только для активных кампаний и если пользователь еще не принял условия */}
        {!activation.showRewardCongratulations && campaign.settings && campaign.settings.isActive === true && terms.isVisible && !campaign.settings.termsConfirm && (
          <TermsAgreement />
        )}
        
        {/* Основной контент показываем только для активных кампаний и после согласия с условиями */}
        {!activation.showRewardCongratulations && campaign.settings && campaign.settings.isActive === true && (!terms.isVisible || campaign.settings.termsConfirm) && (
              <>
                <div
                  className={`promo-section${promoImageUrl ? ' promo-section--has-image' : ''}${showDeepLinkCode ? ' promo-section--with-code' : ''}`}
                >
                  {promoImageUrl ? (
                    <div className="promo-image-wrapper">
                      <img
                        src={promoImageUrl}
                        alt="Промо изображение"
                        className="promo-image"
                      />
                      {showDeepLinkCode && deepLinkDisplayCode && (
                        <DeepLinkCodeBadge code={deepLinkDisplayCode} />
                      )}
                    </div>
                  ) : (
                    <>
                      {campaign.settings.welcomeMessage ? (
                        <p className="promo-welcome-message">{campaign.settings.welcomeMessage}</p>
                      ) : null}
                      {showDeepLinkCode && deepLinkDisplayCode && (
                        <DeepLinkCodeBadge code={deepLinkDisplayCode} />
                      )}
                    </>
                  )}
                </div>       

                {user.isTelegramAvailable && needsPhoneVerification && (
                  <div className="contact-section">
                   {/* <button 
                      className="contact-button"
                      onClick={handleRequestContact}
                      disabled={isActivating || isUpsertingUser}
                    >
                      📞 Поделиться номером телефона
                    </button>*/}
                    <p className="contact-hint">
                    Нам надо убедиться, что ты не робот и приз будет отправлен именно 
тебе.
<br/> 
 Подтверди свой номер телефона, привязанный к Telegram.   
 <br/> <br/>
 👇 Нажми кнопку ниже, чтобы поделиться номером. 
                    </p>
                  </div>
                )}
              </>
            )}
         {/*
       <div> {`contactRequested: ${user.contactRequested}, userHasPhone: ${user.userHasPhone}, username: ${user.user?.username}, userExists: ${user.userExists}, isCheckingUser: ${user.isCheckingUser}`}</div>
         */}

        {/* Сообщения */}
        {/*activation.message && (
          <div className="message">
            <p>{activation.message}</p>
          </div>
        )}*/}

        {/* Тестовый компонент для демонстрации ApiClient */}
        {/*(process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).has('debug')) && <TestApiClient />*/}

        {/* VConsole компонент для отладки (только в режиме разработки или с параметром debug=1) */}
        {(process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).has('debug')) && <VConsole />}
        
        {/* Отладочная информация о настройках кампании */}
        {/*(process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).has('debug')) && campaign.settings && (
          <div style={{ 
            position: 'fixed', 
            bottom: '10px', 
            right: '10px', 
            background: 'rgba(0,0,0,0.8)', 
            color: 'white', 
            padding: '10px', 
            borderRadius: '5px',
            fontSize: '12px',
            maxWidth: '300px',
            zIndex: 0
          }}>
            <div><strong>Campaign Settings:</strong></div>
            <div>ID: {campaign.settings.campaignId}</div>
            <div>IsActive: {campaign.settings.isActive === true ? 'Yes' : campaign.settings.isActive === false ? 'No' : 'null'}</div>
            <div>User Exists: {campaign.settings.userExists ? 'Yes' : 'No'}</div>
            <div>Phone Exists: {campaign.settings.phoneExists ? 'Yes' : 'No'}</div>
            <div>Terms Confirm: {campaign.settings.termsConfirm ? 'Yes' : 'No'}</div>
            <div>Contact Requested: {user.contactRequested ? 'Yes' : 'No'}</div>
            <div>User Has Phone: {user.userHasPhone ? 'Yes' : 'No'}</div>
            {campaign.settings.welcomeMessage && <div>Welcome: {campaign.settings.welcomeMessage}</div>}
            {campaign.settings.buttonColor && <div>Button Color: {campaign.settings.buttonColor}</div>}
          </div>
        )*/}
        
        {/* Компонент для отладки состояния TermsAgreement */}
        {/*(process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).has('debug')) && <TermsAgreementTest />*/}

    </div>
  );
}

export default App;
